import React, { useState } from 'react';
import { useAuth } from '../../lib/auth';

/**
 * Wrap any admin-only page tree. Three states:
 *   1. loading        → neutral spinner
 *   2. signed-out     → Google sign-in button
 *   3. signed-in but not an admin → "not authorized" + sign-out
 *   4. admin          → render children
 *
 * Firestore rules enforce the bootstrap allowlist plus `admin/{email}`
 * docs on the server, so this gate is purely UX — a non-admin who
 * bypasses it still can't write admin collections.
 */
export const AdminGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const {
    user,
    loading,
    isAdmin,
    isFirebaseConfigured,
    signInWithEmail,
    signInWithGoogle,
    sendPasswordReset,
    signOutUser,
  } = useAuth();

  if (!isFirebaseConfigured) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 p-6">
        <div className="max-w-lg w-full bg-white border border-neutral-200 rounded-xl p-8 shadow-sm">
          <div className="text-2xl font-bold text-neutral-900 mb-2">Admin setup — 2 minutes</div>
          <div className="text-sm text-neutral-600 mb-5">
            Sign-in needs a Firebase Web App registered for{' '}
            <span className="font-mono">parrot-fe170</span>. Once that exists, paste two values
            into your hosting env vars and redeploy.
          </div>
          <ol className="text-sm text-neutral-700 space-y-3 mb-5 list-decimal list-inside">
            <li>
              Open{' '}
              <a
                className="text-blue-600 underline"
                href="https://console.firebase.google.com/project/parrot-fe170/settings/general"
                target="_blank"
                rel="noreferrer"
              >
                Firebase Console → Project Settings
              </a>{' '}
              → Your apps → <span className="font-semibold">Add app → Web</span>. Name it
              "Parrot Admin", skip Hosting setup.
            </li>
            <li>
              Copy <span className="font-mono">apiKey</span> and{' '}
              <span className="font-mono">appId</span> from the snippet it shows.
            </li>
            <li>
              Add to your hosting env (Vercel / Firebase Hosting / wherever):
              <pre className="mt-2 text-xs font-mono text-neutral-700 bg-neutral-50 border border-neutral-200 rounded p-3 whitespace-pre-wrap">
{`REACT_APP_FIREBASE_API_KEY=AIza…
REACT_APP_FIREBASE_APP_ID=1:1010612869053:web:…`}
              </pre>
            </li>
            <li>
              In Firebase Console →{' '}
              <a
                className="text-blue-600 underline"
                href="https://console.firebase.google.com/project/parrot-fe170/authentication/providers"
                target="_blank"
                rel="noreferrer"
              >
                Authentication → Sign-in method
              </a>{' '}
              → enable <span className="font-semibold">Google</span>. Add{' '}
              <span className="font-mono">speakparrot.com</span> under{' '}
              <span className="font-semibold">Authorized domains</span>.
            </li>
            <li>Redeploy. Sign-in will work for tremaine.grant@gmail.com.</li>
          </ol>
          <div className="text-xs text-neutral-400">
            Marketing pages keep working without this — only /admin/* requires it.
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="text-sm text-neutral-500">Checking access…</div>
      </div>
    );
  }

  if (!user) {
    return <SignInPanel
      onEmailSignIn={signInWithEmail}
      onGoogleSignIn={signInWithGoogle}
      onPasswordReset={sendPasswordReset}
    />;
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 p-6">
        <div className="max-w-md w-full bg-white border border-neutral-200 rounded-xl p-8 shadow-sm text-center">
          <div className="text-2xl font-bold text-neutral-900 mb-2">Not authorized</div>
          <div className="text-sm text-neutral-500 mb-6">
            <span className="font-mono">{user.email}</span> isn't an admin.
          </div>
          <button
            onClick={signOutUser}
            className="text-sm text-neutral-600 underline hover:text-neutral-900"
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

/**
 * Email/password sign-in (same Firebase Auth as the iOS Parrot app —
 * credentials you set up on iOS work here unchanged). Google popup is
 * kept as a fallback for accounts created via Apple/Google that don't
 * have an email/password yet.
 */
const SignInPanel: React.FC<{
  onEmailSignIn: (email: string, password: string) => Promise<void>;
  onGoogleSignIn: () => Promise<void>;
  onPasswordReset: (email: string) => Promise<void>;
}> = ({ onEmailSignIn, onGoogleSignIn, onPasswordReset }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState<'email' | 'google' | 'reset' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resetMessage, setResetMessage] = useState<string | null>(null);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (!email.trim() || !password) {
      setError('Enter your email and password.');
      return;
    }
    setBusy('email');
    setError(null);
    setResetMessage(null);
    try {
      await onEmailSignIn(email, password);
    } catch (err: any) {
      setError(prettyAuthError(err));
    } finally {
      setBusy(null);
    }
  };

  const handleGoogle = async () => {
    if (busy) return;
    setBusy('google');
    setError(null);
    setResetMessage(null);
    try {
      await onGoogleSignIn();
    } catch (err: any) {
      setError(prettyAuthError(err));
    } finally {
      setBusy(null);
    }
  };

  const handleReset = async () => {
    if (busy) return;
    if (!email.trim()) {
      setError('Enter your email above first — we\'ll send the reset link there.');
      return;
    }
    setBusy('reset');
    setError(null);
    setResetMessage(null);
    try {
      await onPasswordReset(email);
      setResetMessage(`Reset link sent to ${email.trim()}. Check your inbox.`);
    } catch (err: any) {
      setError(prettyAuthError(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50 p-6">
      <div className="max-w-sm w-full bg-white border border-neutral-200 rounded-xl p-8 shadow-sm">
        <div className="text-2xl font-bold text-neutral-900 mb-1">Parrot admin</div>
        <div className="text-sm text-neutral-500 mb-6">
          Sign in with the same email + password you use in the Parrot iOS app.
        </div>

        <form onSubmit={handleEmailSubmit} className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-neutral-600 mb-1">Email</label>
            <input
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full px-3 py-2.5 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:border-neutral-400"
              disabled={!!busy}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-neutral-600 mb-1">Password</label>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full px-3 py-2.5 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:border-neutral-400"
              disabled={!!busy}
            />
          </div>

          {error && (
            <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
              {error}
            </div>
          )}
          {resetMessage && (
            <div className="text-xs text-green-700 bg-green-50 border border-green-200 rounded-md px-3 py-2">
              {resetMessage}
            </div>
          )}

          <button
            type="submit"
            disabled={!!busy}
            className="w-full bg-neutral-900 text-white rounded-lg py-2.5 text-sm font-semibold hover:bg-neutral-800 transition disabled:opacity-60"
          >
            {busy === 'email' ? 'Signing in…' : 'Sign in'}
          </button>

          <div className="flex items-center justify-between text-xs">
            <button
              type="button"
              onClick={handleReset}
              disabled={!!busy}
              className="text-neutral-500 hover:text-neutral-900 underline disabled:opacity-60"
            >
              {busy === 'reset' ? 'Sending…' : 'Forgot password?'}
            </button>
          </div>
        </form>

        <div className="my-5 flex items-center gap-3">
          <div className="flex-1 h-px bg-neutral-200" />
          <div className="text-xs text-neutral-400">or</div>
          <div className="flex-1 h-px bg-neutral-200" />
        </div>

        <button
          onClick={handleGoogle}
          disabled={!!busy}
          className="w-full bg-white border border-neutral-200 text-neutral-900 rounded-lg py-2.5 text-sm font-semibold hover:border-neutral-400 transition disabled:opacity-60"
        >
          {busy === 'google' ? 'Opening Google…' : 'Continue with Google'}
        </button>
      </div>
    </div>
  );
};

/// Maps Firebase Auth error codes to copy that's useful in the admin.
/// Defaults to the raw message when the code is unfamiliar so we don't
/// hide a useful detail behind a generic string.
function prettyAuthError(err: unknown): string {
  const code = (err as { code?: string })?.code;
  switch (code) {
    case 'auth/invalid-email':
      return 'That email doesn\'t look right.';
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return 'Email or password is incorrect.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Try again in a minute, or reset your password.';
    case 'auth/network-request-failed':
      return 'Network error. Check your connection and try again.';
    case 'auth/popup-closed-by-user':
      return 'Google sign-in was cancelled.';
    case 'auth/missing-password':
      return 'Enter your password to sign in.';
    default:
      return (err as Error)?.message || 'Sign-in failed.';
  }
}
